import { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { PageContainer } from '@/components/layout/PageContainer';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePatientQuery } from '@/hooks/usePatientsQueries';
import { useTreatmentsQuery } from '@/hooks/useTreatmentLibraryQueries';
import { useTreatmentPlanTemplatesQuery } from '@/hooks/useTreatmentPlanTemplates';
import {
  usePatientTreatmentPlanQuery,
  usePatientTreatmentPlanVersionsQuery,
  useSaveTreatmentPlanMutation,
  useShareTreatmentPlanMutation,
  useTreatmentPlanVersionQuery,
  useUnshareTreatmentPlanMutation,
} from '@/hooks/useTreatmentPlan';
import type {
  TreatmentPlan,
  TreatmentPlanVersionSummary,
  TreatmentPlanWritePayload,
} from '@/api/treatmentPlans/treatmentPlansApi.types';
import type { Treatment } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import type { TreatmentPlanTemplate } from '@/api/treatmentPlanTemplates/treatmentPlanTemplatesApi.types';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { parseISO } from 'date-fns';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { TreatmentItemRow } from './TreatmentItemRow';
import { ExerciseRow } from './ExerciseRow';
import type { EditorPlanExercise, EditorPlanItem } from './types';

function localizedName(nameEn: string, namePl: string): string {
  if (i18next.language === 'pl' && namePl.length > 0) {
    return namePl;
  }
  return nameEn;
}

let localRowKeyCounter = 0;
function nextRowKey(): string {
  localRowKeyCounter = localRowKeyCounter + 1;
  return 'plan-row-' + localRowKeyCounter;
}

export function TreatmentPlanEditor() {
  const { t } = useTranslation(['plans', 'common']);
  const navigate = useNavigate();
  const { patientId, planId } = useParams<{ patientId: string; planId: string }>();
  const resolvedPatientId = patientId === undefined ? '' : patientId;
  const routePlanId = planId === undefined ? 'current' : planId;
  const isCurrent = routePlanId === 'current';

  const patientQuery = usePatientQuery(resolvedPatientId);
  const treatmentsQuery = useTreatmentsQuery('', null, false, isCurrent);
  const templatesQuery = useTreatmentPlanTemplatesQuery(isCurrent);
  const activePlanQuery = usePatientTreatmentPlanQuery(resolvedPatientId);
  const versionQuery = useTreatmentPlanVersionQuery(routePlanId, !isCurrent);
  const versionsQuery = usePatientTreatmentPlanVersionsQuery(resolvedPatientId);

  const saveMutation = useSaveTreatmentPlanMutation();
  const shareMutation = useShareTreatmentPlanMutation();
  const unshareMutation = useUnshareTreatmentPlanMutation();

  const [isInitialized, setIsInitialized] = useState(false);
  const [planIdentifier, setPlanIdentifier] = useState<string | null>(null);
  const [planVersion, setPlanVersion] = useState<number | null>(null);
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [therapistNotes, setTherapistNotes] = useState('');
  const [clientVisibleAt, setClientVisibleAt] = useState<string | null>(null);
  const [items, setItems] = useState<EditorPlanItem[]>([]);
  const [exercises, setExercises] = useState<EditorPlanExercise[]>([]);
  const [pickerValue, setPickerValue] = useState('');
  const [templatePickerValue, setTemplatePickerValue] = useState('');
  const [isStopSharingConfirmOpen, setIsStopSharingConfirmOpen] = useState(false);
  const [baselineSnapshot, setBaselineSnapshot] = useState<string | null>(null);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  const libraryTreatments = treatmentsQuery.data === undefined ? [] : treatmentsQuery.data;
  const planTemplates = templatesQuery.data === undefined ? [] : templatesQuery.data;

  const loadFromPlan = useCallback(function load(plan: TreatmentPlan | null) {
    if (plan === null) {
      setPlanIdentifier(null);
      setPlanVersion(null);
      setPlanStatus(null);
      setTherapistNotes('');
      setClientVisibleAt(null);
      setItems([]);
      setExercises([]);
      return;
    }
    setPlanIdentifier(plan.id);
    setPlanVersion(plan.version);
    setPlanStatus(plan.status);
    setTherapistNotes(plan.therapistNotes === null ? '' : plan.therapistNotes);
    setClientVisibleAt(plan.clientVisibleAt);
    setItems(
      plan.items.map(function toEditor(item) {
        return {
          key: nextRowKey(),
          treatmentId: item.treatmentId,
          treatmentName: item.treatmentName,
          instructions: item.instructions === null ? '' : item.instructions,
          progress: item.progress,
        };
      })
    );
    setExercises(
      plan.exercises.map(function toEditor(exercise) {
        return {
          key: nextRowKey(),
          name: exercise.name,
          instructions: exercise.instructions === null ? '' : exercise.instructions,
          frequency: exercise.frequency === null ? '' : exercise.frequency,
          compliance: exercise.compliance,
        };
      })
    );
  }, []);

  useEffect(
    function initialize() {
      if (isInitialized) {
        return;
      }
      if (isCurrent) {
        if (activePlanQuery.isLoading) {
          return;
        }
        loadFromPlan(activePlanQuery.data === undefined ? null : activePlanQuery.data);
        setIsInitialized(true);
        return;
      }
      if (versionQuery.data === undefined) {
        return;
      }
      loadFromPlan(versionQuery.data);
      setIsInitialized(true);
    },
    [
      isInitialized,
      isCurrent,
      activePlanQuery.isLoading,
      activePlanQuery.data,
      versionQuery.data,
      loadFromPlan,
    ]
  );

  function addTreatment(treatment: Treatment) {
    setItems(function append(current) {
      return [
        ...current,
        {
          key: nextRowKey(),
          treatmentId: treatment.id,
          treatmentName: localizedName(treatment.nameEn, treatment.namePl),
          instructions: '',
          progress: null,
        },
      ];
    });
  }

  function addExercise() {
    setExercises(function append(current) {
      return [
        ...current,
        { key: nextRowKey(), name: '', instructions: '', frequency: '', compliance: null },
      ];
    });
  }

  function startFromTemplate(template: TreatmentPlanTemplate) {
    setItems(
      template.items.map(function toEditor(item) {
        return {
          key: nextRowKey(),
          treatmentId: item.treatmentId,
          treatmentName: item.treatmentName,
          instructions: item.instructions === null ? '' : item.instructions,
          progress: null,
        };
      })
    );
    setExercises(
      template.exercises.map(function toEditor(exercise) {
        return {
          key: nextRowKey(),
          name: exercise.name,
          instructions: exercise.instructions === null ? '' : exercise.instructions,
          frequency: exercise.frequency === null ? '' : exercise.frequency,
          compliance: null,
        };
      })
    );
  }

  function buildPayload(): TreatmentPlanWritePayload {
    return {
      therapistNotes: therapistNotes.trim() === '' ? null : therapistNotes,
      items: items.map(function toInput(item, index) {
        return {
          treatmentId: item.treatmentId,
          instructions: item.instructions.trim() === '' ? null : item.instructions,
          sortOrder: index,
        };
      }),
      exercises: exercises
        .filter(function hasName(exercise) {
          return exercise.name.trim().length > 0;
        })
        .map(function toInput(exercise, index) {
          return {
            name: exercise.name.trim(),
            instructions: exercise.instructions.trim() === '' ? null : exercise.instructions,
            frequency: exercise.frequency.trim() === '' ? null : exercise.frequency,
            sortOrder: index,
          };
        }),
    };
  }

  async function save() {
    try {
      const saved = await saveMutation.mutateAsync({
        patientId: resolvedPatientId,
        payload: buildPayload(),
      });
      loadFromPlan(saved);
      setBaselineSnapshot(null);
      showSuccessNotification(t('plans:editor.saved'));
    } catch {
      showErrorNotification(t('plans:editor.saveError'));
    }
  }

  async function share() {
    try {
      const shared = await shareMutation.mutateAsync(resolvedPatientId);
      loadFromPlan(shared);
      showSuccessNotification(t('plans:editor.shared'));
    } catch {
      showErrorNotification(t('plans:editor.shareError'));
    }
  }

  async function stopSharing() {
    try {
      const updated = await unshareMutation.mutateAsync(resolvedPatientId);
      loadFromPlan(updated);
      setIsStopSharingConfirmOpen(false);
      showSuccessNotification(t('plans:editor.unshared'));
    } catch {
      showErrorNotification(t('plans:editor.shareError'));
    }
  }

  function openVersion(version: TreatmentPlanVersionSummary) {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientTreatmentPlan, {
        patientId: resolvedPatientId,
        planId: version.status === 'active' ? 'current' : version.id,
      })
    );
  }

  const isReadOnly = isCurrent === false;
  const isBusy = saveMutation.isPending || shareMutation.isPending || unshareMutation.isPending;
  const isSaving = saveMutation.isPending;
  const isSharedWithClient = clientVisibleAt !== null;
  const hasSavedPlan = planIdentifier !== null;
  const patientName = patientQuery.data === undefined ? '' : patientQuery.data.name;
  const versions = versionsQuery.data === undefined ? [] : versionsQuery.data;

  const currentSnapshot = JSON.stringify({ therapistNotes, items, exercises });
  const isDirty =
    isInitialized &&
    isReadOnly === false &&
    baselineSnapshot !== null &&
    currentSnapshot !== baselineSnapshot;

  useEffect(
    function captureBaselineSnapshot() {
      if (isInitialized && baselineSnapshot === null) {
        setBaselineSnapshot(currentSnapshot);
      }
    },
    [isInitialized, baselineSnapshot, currentSnapshot]
  );

  function navigateBackToPatient() {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
        patientId: resolvedPatientId,
      })
    );
  }

  function handleBackClick() {
    if (isDirty) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    navigateBackToPatient();
  }

  const isLoading =
    patientQuery.isLoading ||
    (isCurrent && (activePlanQuery.isLoading || treatmentsQuery.isLoading)) ||
    (isReadOnly && versionQuery.isLoading) ||
    !isInitialized;

  const isError =
    patientQuery.isError ||
    (isCurrent && (activePlanQuery.isError || treatmentsQuery.isError)) ||
    (isReadOnly && versionQuery.isError);

  const footer = isLoading || isReadOnly ? undefined : (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        {isDirty ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-zoowarningtext">
            <Icon name="AlertCircle" />
            {t('plans:editor.unsavedChanges')}
          </span>
        ) : null}
      </div>
      <Button
        onClick={function onSave() {
          void save();
        }}
        disabled={isBusy}
        isLoading={isSaving}
        className="flex items-center gap-2"
      >
        {isSaving ? t('plans:editor.saving') : t('plans:editor.save')}
      </Button>
    </div>
  );

  return (
    <PageContainer footer={footer} width="wide">
      <Button
        variant="link"
        onClick={handleBackClick}
        className="text-zoomuted hover:text-zooprimary transition-colors mb-4"
      >
        <Icon name="ArrowLeft" />
        {t('plans:editor.back')}
      </Button>

      {isError ? (
        <QueryErrorState
          onRetry={function retry() {
            patientQuery.refetch();
            treatmentsQuery.refetch();
            activePlanQuery.refetch();
            versionQuery.refetch();
          }}
        />
      ) : isLoading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
            <div>
              <h1 className="text-2xl font-bold text-zooindigo">{t('plans:editor.title')}</h1>
              {patientName.length === 0 ? null : (
                <p className="text-sm text-zoomuted mt-0.5">{patientName}</p>
              )}
            </div>
            {planVersion === null ? null : (
              <Badge tone={planStatus === 'active' ? 'success' : 'muted'} size="md">
                {t('plans:editor.versionLabel', { version: planVersion })}
                {' · '}
                {planStatus === 'active'
                  ? t('plans:editor.statusActive')
                  : t('plans:editor.statusSuperseded')}
              </Badge>
            )}
          </div>

          <div className="space-y-5">
            {isReadOnly ? (
              <div className="bg-zoowarningbg rounded-2xl p-4 flex items-start gap-3">
                <Icon name="Eye" className="text-zoowarningtext text-lg mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-zooindigo">
                    {t('plans:editor.readOnlyBanner')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="chip"
                  onClick={function backToCurrent() {
                    navigate(
                      generatePath(APPLICATION_ROUTES.workspacePatientTreatmentPlan, {
                        patientId: resolvedPatientId,
                        planId: 'current',
                      })
                    );
                  }}
                  className="shrink-0"
                >
                  {t('plans:editor.backToCurrent')}
                </Button>
              </div>
            ) : null}

            {isCurrent && hasSavedPlan === false ? (
              <p className="text-xs text-zoomuted">{t('plans:editor.noPlanYet')}</p>
            ) : null}

            {isCurrent &&
            items.length === 0 &&
            exercises.length === 0 &&
            planTemplates.length > 0 ? (
              <div className="bg-zoovpalelavender bg-opacity-30 border border-zooviolet rounded-2xl p-4">
                <p className="text-sm font-bold text-zooindigo mb-2">
                  {t('plans:templates.startFromTemplate')}
                </p>
                <SearchableSelect
                  inputId="treatment-plan-template-picker"
                  ariaLabel={t('plans:templates.startFromTemplate')}
                  placeholder={t('plans:templates.chooseTemplate')}
                  value={templatePickerValue}
                  isDisabled={isBusy}
                  options={planTemplates.map(function toOption(template) {
                    return {
                      value: template.code,
                      label: localizedName(template.nameEn, template.namePl),
                    };
                  })}
                  onChange={function pickTemplate(nextValue) {
                    const template = planTemplates.find(function match(candidate) {
                      return candidate.code === nextValue;
                    });
                    if (template !== undefined) {
                      startFromTemplate(template);
                    }
                    setTemplatePickerValue('');
                  }}
                />
              </div>
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
              <div className="lg:col-span-2 space-y-5">
            <Card>
              <p className="text-sm font-bold text-zooindigo mb-3">
                {t('plans:editor.treatments')}
              </p>
              {isReadOnly ? null : (
                <SearchableSelect
                  inputId="treatment-plan-treatment-picker"
                  ariaLabel={t('plans:editor.addTreatment')}
                  placeholder={t('plans:editor.addTreatment')}
                  value={pickerValue}
                  isDisabled={isBusy}
                  options={libraryTreatments.map(function toOption(treatment) {
                    return {
                      value: treatment.id,
                      label: localizedName(treatment.nameEn, treatment.namePl),
                    };
                  })}
                  onChange={function pickTreatment(nextValue) {
                    const treatment = libraryTreatments.find(function match(candidate) {
                      return candidate.id === nextValue;
                    });
                    if (treatment !== undefined) {
                      addTreatment(treatment);
                    }
                    setPickerValue('');
                  }}
                />
              )}
              {items.length === 0 ? (
                <p className="text-xs text-zoomuted mt-3">{t('plans:editor.noTreatments')}</p>
              ) : (
                <div className="space-y-3 mt-3">
                  {items.map(function renderItem(item) {
                    return (
                      <TreatmentItemRow
                        key={item.key}
                        item={item}
                        isDisabled={isBusy}
                        isReadOnly={isReadOnly}
                        onChange={function change(updated) {
                          setItems(function apply(current) {
                            return current.map(function replace(existing) {
                              return existing.key === updated.key ? updated : existing;
                            });
                          });
                        }}
                        onRemove={function remove() {
                          setItems(function drop(current) {
                            return current.filter(function keep(existing) {
                              return existing.key !== item.key;
                            });
                          });
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-zooindigo">{t('plans:editor.exercises')}</p>
                {isReadOnly ? null : (
                  <Button variant="link" disabled={isBusy} onClick={addExercise} className="text-xs">
                    <Icon name="Plus" />
                    {t('plans:editor.addExercise')}
                  </Button>
                )}
              </div>
              {exercises.length === 0 ? (
                <p className="text-xs text-zoomuted">{t('plans:editor.noExercises')}</p>
              ) : (
                <div className="space-y-3">
                  {exercises.map(function renderExercise(exercise) {
                    return (
                      <ExerciseRow
                        key={exercise.key}
                        exercise={exercise}
                        isDisabled={isBusy}
                        isReadOnly={isReadOnly}
                        onChange={function change(updated) {
                          setExercises(function apply(current) {
                            return current.map(function replace(existing) {
                              return existing.key === updated.key ? updated : existing;
                            });
                          });
                        }}
                        onRemove={function remove() {
                          setExercises(function drop(current) {
                            return current.filter(function keep(existing) {
                              return existing.key !== exercise.key;
                            });
                          });
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </Card>

            <div className="bg-zoowhite border border-zooviolet rounded-2xl shadow-sm p-5">
              <label className="block text-xs font-semibold text-zooviolet">
                {t('plans:editor.therapistNotes')}
                {isReadOnly ? (
                  <p className="mt-1 text-sm text-zooindigo whitespace-pre-wrap">
                    {therapistNotes}
                  </p>
                ) : (
                  <textarea
                    value={therapistNotes}
                    disabled={isBusy}
                    rows={3}
                    onChange={function changeNotes(event) {
                      setTherapistNotes(event.target.value);
                    }}
                    className="mt-1 w-full rounded-xl border border-zooviolet bg-zoovpalelavender bg-opacity-30 px-3 py-2 text-sm text-zooindigo"
                  />
                )}
                <span className="block text-[11px] text-zooviolet mt-1">
                  {t('plans:editor.therapistNotesHint')}
                </span>
              </label>
            </div>
              </div>

              <div className="space-y-5">
            {isReadOnly ? null : (
              <Card className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-8 h-8 rounded-lg bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
                      <Icon name="Send" />
                    </span>
                    <p className="text-sm font-bold text-zooindigo">
                      {t('plans:editor.clientShareTitle')}
                    </p>
                  </div>
                  {isSharedWithClient === true ? (
                    <Badge tone="success" size="md" className="inline-flex items-center gap-1">
                      <Icon name="Check" />
                      {t('plans:editor.sharedBadge')}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-[11px] text-zoomuted">
                  {isSharedWithClient === true
                    ? t('plans:editor.sharedHint')
                    : t('plans:editor.notSharedHint')}
                </p>
                {isSharedWithClient === true ? (
                  <div className="flex items-center justify-end">
                    <Button
                      variant="outline"
                      size="small"
                      onClick={function openStopSharingConfirm() {
                        setIsStopSharingConfirmOpen(true);
                      }}
                      disabled={isBusy}
                    >
                      {t('plans:editor.stopSharing')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-end">
                    <Button
                      size="small"
                      onClick={function onShare() {
                        void share();
                      }}
                      disabled={isBusy || hasSavedPlan === false}
                      isLoading={shareMutation.isPending}
                    >
                      {shareMutation.isPending ? null : <Icon name="Send" />}
                      {t('plans:editor.shareWithClient')}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            <Card>
              <p className="text-sm font-bold text-zooindigo mb-3">
                {t('plans:editor.versionHistory')}
              </p>
              {versions.length === 0 ? (
                <p className="text-xs text-zoomuted">{t('plans:editor.versionHistoryEmpty')}</p>
              ) : (
                <ul className="space-y-2">
                  {versions.map(function renderVersion(version) {
                    return (
                      <li key={version.id}>
                        <button
                          type="button"
                          onClick={function open() {
                            openVersion(version);
                          }}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-zooborder text-left hover:border-zooprimary transition-colors"
                        >
                          <span className="text-sm font-semibold text-zooindigo">
                            {t('plans:editor.versionLabel', { version: version.version })}
                            {' · '}
                            {version.status === 'active'
                              ? t('plans:editor.statusActive')
                              : t('plans:editor.statusSuperseded')}
                          </span>
                          <span className="text-xs text-zoomuted shrink-0">
                            {formatDateToDayMonthYear(parseISO(version.createdAt))}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
              </div>
            </div>
          </div>

          {isStopSharingConfirmOpen === true ? (
            <ConfirmDialog
              title={t('plans:editor.stopSharingConfirmTitle')}
              description={t('plans:editor.stopSharingConfirmDescription')}
              confirmLabel={t('plans:editor.stopSharing')}
              iconName="Eye"
              isConfirming={unshareMutation.isPending}
              onConfirm={function confirmStopSharing() {
                void stopSharing();
              }}
              onCancel={function cancelStopSharing() {
                setIsStopSharingConfirmOpen(false);
              }}
            />
          ) : null}

          {isLeaveConfirmOpen === true ? (
            <ConfirmDialog
              title={t('plans:editor.leaveTitle')}
              description={t('plans:editor.leaveMessage')}
              confirmLabel={t('plans:editor.leaveConfirm')}
              tone="danger"
              iconName="AlertCircle"
              onConfirm={navigateBackToPatient}
              onCancel={function cancelLeave() {
                setIsLeaveConfirmOpen(false);
              }}
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
}
